import React from 'react';
import styles from './Confirmation.module.css';
import shared from '../../styles/shared.module.css';
import {
  step1Fields, undergradFields,
  graduateNationOptions, graduateTravelTimeField,
} from "../../data/formFields";
import { labelFor } from "../../Utilities/optionLabels";

const PROGRAMME_NAMES = {
  volunteer: 'Global Volunteer',
  intern: 'Global Talent',
  teach: 'Global Teacher',
};
const TRACK_NATION_FIELDS = {
  volunteer: 'gvNations',
  intern: 'gtaNations',
  teach: 'gteNations',
};
const findField = (fields, name) => fields.find((f) => f.name === name);

const renderCountriesWithFlags = (options, values) => {
  if (!Array.isArray(values) || values.length === 0) return '—';
  return (
    <div className={styles.countryList}>
      {values.map((v) => {
        const opt = options.find((o) => o.value === v);
        if (!opt) return null;
        return (
          <span key={v} className={styles.countryItem}>
            {opt.countryCode && (
              <span
                className={`fi fis fi-${opt.countryCode} ${styles.flagSmall}`}
              />
            )}
            {opt.label}
          </span>
        );
      })}
    </div>
  );
};

function Confirmation({
  data,
  onEdit,
  onSubmit,
  onBack,
  isSubmitting,
  submitError,
}) {
  const isUndergrad = data.educationLevel === 'undergraduate';
  const educationField = findField(step1Fields, 'educationLevel');
  const selectedTracks = isUndergrad ? ['volunteer'] : (Array.isArray(data.opportunityType) ? data.opportunityType : []);
  const programmeNames = selectedTracks.map((track) => PROGRAMME_NAMES[track]).filter(Boolean);

let nationsDisplay = "—", workFieldOptions, travelTimeOptions;
const nationOptions = isUndergrad ? findField(undergradFields, "nations").options : graduateNationOptions; // graduateNationOptions needs exporting from formFields.js if not already
workFieldOptions = findField(undergradFields, "workField").options;
if (isUndergrad) {
  travelTimeOptions = findField(undergradFields, "travelTime").options;
} else {
  travelTimeOptions = graduateTravelTimeField.options;
}
const countrySelections = isUndergrad
  ? [{ track: 'volunteer', values: data.nations }]
  : selectedTracks.map((track) => ({ track, values: data[TRACK_NATION_FIELDS[track]] }));
nationsDisplay = (
  <div className={styles.countryTracks}>
    {countrySelections.map(({ track, values }) => (
      <div key={track} className={styles.countryBlock}>
        {!isUndergrad && <span className={styles.countryLabel}>{PROGRAMME_NAMES[track]}</span>}
        {renderCountriesWithFlags(nationOptions, values)}
      </div>
    ))}
  </div>
);

// Resolve "Other" fill-ins to the text the applicant typed.
const workFieldDisplay = Array.isArray(data.workField) && data.workField.length
  ? data.workField.map((v) => (v === "other" ? (data.workFieldOther || "Other") : labelFor(workFieldOptions, v))).join(", ")
  : "—";
const fundingDisplay = data.funding || "—";

  return (
    <div className={styles.wrapper}>
      <h1 className={shared.pageHeading}>Review your application.</h1>
      <p className={shared.pageSubheading}>
        Check everything looks correct before submitting. You can edit any
        section.
      </p>

      <div className={styles.sectionsStack}>
        <div className={shared.fieldsCard}>
          <div className={styles.sectionHeader}>
            <h3>Personal information</h3>
            <button
              type="button"
              className={styles.editButton}
              onClick={() => onEdit(1)}
            >
              Edit
            </button>
          </div>
          <dl className={styles.dl}>
            <dt>Full name</dt>
            <dd>{data.fullName || '—'}</dd>
            <dt>Email</dt>
            <dd>{data.email || '—'}</dd>
            <dt>Phone</dt>
            <dd>{data.phoneNumber || '—'}</dd>
            <dt>Age</dt>
            <dd>{data.age || '—'}</dd>
            <dt>Location</dt>
            <dd>{data.location || '—'}</dd>
            <dt>Education</dt>
            <dd>{labelFor(educationField.options, data.educationLevel)}</dd>
          </dl>
        </div>

        <div className={shared.fieldsCard}>
          <div className={styles.sectionHeader}>
            <h3>Exchange Programmes</h3>
            <button
              type="button"
              className={styles.editButton}
              onClick={() => onEdit(2)}
            >
              Edit
            </button>
          </div>
          <dl className={styles.dl}>
            <dt>Selected Tracks</dt>
            <dd className={styles.trackValue}>{programmeNames.length ? programmeNames.join(', ') : '—'}</dd>
          </dl>
        </div>

        <div className={shared.fieldsCard}>
          <div className={styles.sectionHeader}>
    <h3>Your preferences</h3>
    <button type="button" className={styles.editButton} onClick={() => onEdit(2)}>Edit</button>
  </div>

  <div className={styles.countryBlock}>
    <span className={styles.countryLabel}>Preferred countries</span>
    {nationsDisplay}
  </div>

  <dl className={styles.dl}>
    <dt>Work field</dt><dd>{workFieldDisplay}</dd>
    <dt>Duration</dt><dd>{labelFor(travelTimeOptions, data.travelTime)}</dd>
    <dt>Funding</dt><dd>{fundingDisplay}</dd>
  </dl>
        </div>

        <div className={shared.fieldsCard}>
          <div className={styles.sectionHeader}>
            <h3>Your application</h3>
            <button
              type="button"
              className={styles.editButton}
              onClick={() => onEdit(3)}
            >
              Edit
            </button>
          </div>
          <div className={styles.rowLine}>
            <span className={styles.rowLabel}>CV</span>
            <span className={`${styles.rowValue} ${styles.cvValue}`}>{data.cv ? data.cv.name : '—'}</span>
          </div>
          <div className={styles.textBlock}>
            <span className={styles.textBlockLabel}>Motivation</span>
            <p className={styles.textBlockValue}>{data.motivation || '—'}</p>
          </div>
          <div className={styles.textBlock}>
            <span className={styles.textBlockLabel}>Additional comments</span>
            <p className={styles.textBlockValue}>{data.additionalComments || 'Nil'}</p>
          </div>
        </div>
      </div>

      {submitError && <p className={styles.submitError}>{submitError}</p>}

      <div className={shared.navRow}>
        <button
          type="button"
          className={shared.secondaryButton}
          onClick={onBack}
          disabled={isSubmitting}
        >
          ← Back
        </button>
        <button
          type="button"
          className={shared.primaryButton}
          onClick={onSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Application →'}
        </button>
      </div>
    </div>
  );
}

export default Confirmation;
